import { useSyncExternalStore } from "react";

// A single refcounted interval per step feeds every subscriber a `now`
// (floored to the step) via useSyncExternalStore, so any number of concurrent
// running pills share one interval rather than each owning a drifting one,
// React coalesces their per-step updates, and only leaf nodes that read the
// clock re-render.

// No-op subscription for disabled callers: the hook must run unconditionally
// (rules of hooks) but a non-running pill never joins the interval.
function subscribeDisabled(): () => void {
  return () => {};
}

// SSR: a stable value avoids a hydration mismatch; the live label resolves on
// the client once the interval attaches.
function getServerSnapshot(): number {
  return 0;
}

function createNowTicker(stepMs: number) {
  function flooredNow(): number {
    return Math.floor(Date.now() / stepMs) * stepMs;
  }

  let intervalId: ReturnType<typeof setInterval> | null = null;
  let currentNow = flooredNow();
  const listeners = new Set<() => void>();

  function tick(): void {
    const next = flooredNow();
    // Skip when the step hasn't advanced: a stable snapshot keeps
    // useSyncExternalStore from re-rendering subscribers needlessly.
    if (next === currentNow) return;
    currentNow = next;
    listeners.forEach((listener) => listener());
  }

  function startIfNeeded(): void {
    if (intervalId !== null) return;
    // Re-seed so the first reader after an idle period sees a fresh step.
    currentNow = flooredNow();
    // Polling every second keeps coarse steps aligned to their boundaries.
    intervalId = setInterval(tick, Math.min(stepMs, 1000));
  }

  function stopIfIdle(): void {
    if (listeners.size > 0 || intervalId === null) return;
    clearInterval(intervalId);
    intervalId = null;
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    startIfNeeded();
    return () => {
      listeners.delete(listener);
      stopIfIdle();
    };
  }

  function getSnapshot(): number {
    // Idle, nothing advances currentNow, so a disabled reader would see the
    // step the interval last stopped on.
    if (intervalId === null) currentNow = flooredNow();
    return currentNow;
  }

  function useTick(enabled: boolean): number {
    return useSyncExternalStore(
      enabled ? subscribe : subscribeDisabled,
      getSnapshot,
      getServerSnapshot,
    );
  }

  return {
    useTick,
    isRunning: () => intervalId !== null,
    subscriberCount: () => listeners.size,
  };
}

const secondTicker = createNowTicker(1000);
const minuteTicker = createNowTicker(60 * 1000);

/**
 * Subscribe to the shared 1s clock. Returns `now` in ms (floored to the
 * second). When `enabled` is false the hook returns the current snapshot
 * without starting or joining the interval.
 */
export function useNowTick(enabled: boolean): number {
  return secondTicker.useTick(enabled);
}

/**
 * Subscribe to the shared 1min clock. Returns `now` in ms (floored to the
 * minute), for derived data whose "now" only needs to roll slowly, e.g. a
 * chart's time-range domain.
 */
export function useNowMinuteTick(enabled: boolean): number {
  return minuteTicker.useTick(enabled);
}

/** Test-only: whether the shared 1s interval is currently running. */
export function __isTickerRunning(): boolean {
  return secondTicker.isRunning();
}

/** Test-only: number of active 1s subscribers. */
export function __subscriberCount(): number {
  return secondTicker.subscriberCount();
}
